import type { InitPlayer, PlayerState } from "./player";
import type { RoleCard, RoleId, RoleTeam } from "./role";

export const DEFAULT_DISCUSSION_DURATION_SECONDS = 300;
export const DEFAULT_VOTE_WINDOW_SECONDS = 10;

export type GamePhase =
  | "setup"
  | "role-peek"
  | "night"
  | "day"
  | "vote"
  | "reveal"
  | "complete";

export type CardPosition =
  | { readonly zone: "player"; readonly playerId: string }
  | { readonly zone: "center"; readonly slotIndex: 0 | 1 | 2 };

export interface NightRoleStep {
  readonly roleId: RoleId;
  readonly status: "pending" | "complete";
  readonly actorPlayerIds: readonly string[];
  readonly completedPlayerIds: readonly string[];
}

export interface AnnouncementStep {
  readonly roleId: RoleId;
  readonly scriptKey: string;
  readonly status: "pending" | "complete";
}

export interface VoteCount {
  readonly playerId: string;
  readonly count: number;
  readonly isTop: boolean;
}

export interface VoteLine {
  readonly voterPlayerId: string;
  readonly targetPlayerId: string;
}

export interface VoteResult {
  readonly voteLines: readonly VoteLine[];
  readonly voteCounts: readonly VoteCount[];
  readonly eliminatedPlayerIds: readonly string[];
}

export type GameOutcomeReason =
  | "werewolf-eliminated"
  | "no-werewolves-no-deaths"
  | "werewolf-survived"
  | "minion-survived"
  | "no-werewolves-village-loss"
  | "tanner-eliminated"
  | "tanner-and-werewolf-eliminated";

export interface GameOutcome {
  readonly winningTeam: RoleTeam;
  readonly winningTeams: readonly RoleTeam[];
  readonly reason: GameOutcomeReason;
  readonly eliminatedPlayerIds: readonly string[];
}

export interface GameState {
  readonly gameId: string;
  readonly seed: string;
  readonly createdAt: string;
  readonly version: number;
  readonly phase: GamePhase;
  readonly hostPlayerId: string;
  readonly players: readonly PlayerState[];
  readonly seatOrder: readonly string[];
  readonly seatLocked: boolean;
  readonly selectedRoleIds: readonly RoleId[];
  readonly cards: readonly RoleCard[];
  readonly cardPositions: Readonly<Record<string, CardPosition>>;
  readonly centerSlots: readonly [string | null, string | null, string | null];
  readonly privateKnowledge: Readonly<Record<string, readonly string[]>>;
  /**
   * Set once the doppelganger copies a role. The doppelganger CARD then
   * counts as the copied role for every end-of-game judgement, no matter who
   * ends up holding it. Optional for backward compatibility with persisted
   * states. PRIVATE — projections must never expose it publicly.
   */
  readonly doppelgangerCopiedRoleId?: RoleId | null;
  readonly rolePeekConfirmedPlayerIds: readonly string[];
  readonly nightRoleQueue: readonly NightRoleStep[];
  readonly announcerTimeline: readonly AnnouncementStep[];
  readonly nightLog: readonly string[];
  readonly discussionDurationSeconds: number;
  readonly discussionRemainingSeconds: number | null;
  readonly voteWindowSeconds: number;
  readonly voteRemainingSeconds: number | null;
  readonly autoVotePlayerIds: readonly string[];
  readonly voteResult: VoteResult | null;
  readonly revealedCardIds: readonly string[];
  readonly gameOutcome: GameOutcome | null;
}

export interface InitGameOptions {
  readonly gameId: string;
  readonly seed: string;
  readonly createdAt: string;
  readonly hostPlayerId: string;
  readonly players: readonly InitPlayer[];
}
